import { FC } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TableBody as Body, SelectChangeEvent } from '@mui/material';
import dayjs from 'dayjs';

import { HeaderProperties } from '../../../shared/uiComponents/Table/tableProperties';
import { Badge, CustomSelect, Text } from '../../../shared/uiComponents';
import { BadgeTypes, SoapNoteStatusTypes, colors } from '../../../shared/uiComponents/StatusBadge';
import { CustomTableRow, RowTableCell } from '../../../shared/uiComponents/Table/tableHelpers';
import CustomDrawer from '../../../shared/uiComponents/Drawer';
import { InfoRow } from '../../../components/StyledComponents';
import { AdminTypes } from '../../../components/Menu';
import Actions from '../Actions';

import { SoapNotesFullProperties } from '../../../redux/API/ClientAPIHelpers/soapNoteProperties';
import { useSelector } from '../../../redux/store';

export const DetailedInfo = ({ note }: { note: SoapNotesFullProperties }) => {
	const { client, user, status, createdAt, createdBy, sessionType, submittedAt, submittedBy, startTime, endTime } =
		note;

	return (
		<>
			<Text title="Client Information:" type="plg" font="header" fontWeight="semiBold" />
			<InfoRow className="paddingTop8 paddingBottom8">
				<Text title="Client name:" type="psm" fontWeight="semiBold" />
				<Text title={client.fullName} type="psm" />
			</InfoRow>
			{Boolean(client.insurances?.length) &&
				client.insurances.map((insurance) => (
					<div key={insurance.type}>
						<InfoRow className="paddingTop8 paddingBottom8">
							<Text
								title={`${insurance.type === 1 ? 'Primary' : 'Secondary'} Insurance:`}
								type="psm"
								fontWeight="semiBold"
							/>
							<Text title={insurance.name as string} type="psm" />
						</InfoRow>
						<InfoRow className="paddingTop8 paddingBottom8">
							<Text
								title={`${insurance.type === 1 ? 'Primary' : 'Secondary'} Member ID:`}
								type="psm"
								fontWeight="semiBold"
							/>
							<Text title={insurance.insuranceNumber} type="psm" />
						</InfoRow>
					</div>
				))}
			<Text title="SOAP Note Info:" type="plg" font="header" fontWeight="semiBold" className="marginTop16" />
			<InfoRow className="paddingTop8 paddingBottom8">
				<Text title="Type:" type="psm" fontWeight="semiBold" />
				<Text title={sessionType.name} type="psm" />
			</InfoRow>
			<InfoRow className="paddingTop8 paddingBottom8">
				<Text title="Created by:" type="psm" fontWeight="semiBold" />
				<Text title={createdBy.fullName} type="psm" />
			</InfoRow>
			<InfoRow className="paddingTop8 paddingBottom8">
				<Text title="Created:" type="psm" fontWeight="semiBold" />
				<Text title={dayjs(createdAt).format('MM/DD/YYYY hh:mm A')} type="psm" />
			</InfoRow>
			<InfoRow className="paddingTop8 paddingBottom8">
				<Text title="Status:" type="psm" fontWeight="semiBold" />
				<Text title={status.name} type="psm" />
			</InfoRow>

			{Boolean(submittedBy) && Boolean(submittedAt) && (
				<>
					<InfoRow className="paddingTop8 paddingBottom8">
						<Text title="Submited by:" type="psm" fontWeight="semiBold" />
						<Text title={submittedBy?.fullName ?? ''} type="psm" />
					</InfoRow>
					<InfoRow className="paddingTop8 paddingBottom8">
						<Text title="Submited:" type="psm" fontWeight="semiBold" />
						<Text title={dayjs(submittedAt).format('MM/DD/YYYY hh:mm A')} type="psm" />
					</InfoRow>
				</>
			)}
			<Text title="Session Info:" type="plg" font="header" fontWeight="semiBold" className="marginTop16" />
			<InfoRow className="paddingTop8 paddingBottom8">
				<Text title="Provider name:" type="psm" fontWeight="semiBold" />
				<Text title={user.fullName} type="psm" />
			</InfoRow>
			<InfoRow className="paddingTop8 paddingBottom8">
				<Text title="Provider role:" type="psm" fontWeight="semiBold" />
				<Text title={user.role.name} type="psm" />
			</InfoRow>

			<InfoRow className="paddingTop8 paddingBottom8">
				<Text title="Started:" type="psm" fontWeight="semiBold" />
				<Text title={dayjs(startTime).format('MM/DD/YYYY hh:mm A')} type="psm" />
			</InfoRow>
			<InfoRow className="paddingTop8 paddingBottom8">
				<Text title="Ended:" type="psm" fontWeight="semiBold" />
				<Text title={dayjs(endTime).format('MM/DD/YYYY hh:mm A')} type="psm" />
			</InfoRow>
		</>
	);
};

export const sessionTypes = [
	{ id: '1', name: '97151 - Assessment', roleType: 1 },
	{ id: '2', name: '97151 or H0032 - Treatment Planning', roleType: 1 },
	{ id: '3', name: '97153 - Behavior Treatment', roleType: 2 },
	{ id: '4', name: '97155 - Direct Supervision', roleType: 1 },
	{ id: '5', name: '97155 - Behavior Treatment Modification', roleType: 1 },
	{ id: '6', name: '97156 - Parent Training', roleType: 1 },
	{ id: '7', name: 'Meet & Greet - 97153', roleType: 2 },
	{ id: '8', name: 'Meet & Greet - Non-Billable', roleType: 2 },
	{ id: '9', name: 'Discretionary Session', roleType: 3 },
];

export interface RowRendererProperties {
	data: SoapNotesFullProperties[];
}

export const Headers: HeaderProperties[] = [
	{ id: '1', name: 'Client Name', orderBy: 'client.fullName' },
	{ id: '2', name: 'Type', orderBy: 'sessionType.name' },
	{ id: '3', name: 'Session Date', orderBy: 'date' },
	{ id: '4', name: 'SOAP Note Created', orderBy: 'createdAt' },
	{ id: '5', name: 'Created by', orderBy: 'createdBy.fullName' },
	{ id: '6', name: 'Status', width: '200px' },
	{ id: '7', name: 'Actions', width: '120px' },
];

export const ClinicalSupervisorHeaders: HeaderProperties[] = [
	{ id: '0', name: 'Provider Name', orderBy: 'user.fullName' },
	...Headers,
];

export const TableBody: FC<RowRendererProperties> = ({ data }) => {
	const role = useSelector((state) => state.account.role);

	return (
		<Body>
			{data.map((row, index) => (
				<CustomTableRow key={index}>
					{role?.section.id === AdminTypes.cs && <RowTableCell>{row.user.fullName}</RowTableCell>}
					<RowTableCell>{row.client.fullName}</RowTableCell>
					<RowTableCell>{row.sessionType.name}</RowTableCell>
					<RowTableCell>{dayjs(row.date).format('MM/DD/YYYY')}</RowTableCell>
					<RowTableCell>{dayjs(row.createdAt).format('MM/DD/YYYY')}</RowTableCell>
					<RowTableCell>{row.createdBy.fullName}</RowTableCell>
					<RowTableCell>
						<Badge label={row.status.name} sizeType="sm" type={SoapNoteStatusTypes[row.status.status] as BadgeTypes} />
					</RowTableCell>
					<RowTableCell>
						<Actions data={row} />
					</RowTableCell>
				</CustomTableRow>
			))}
		</Body>
	);
};

const labels = ['Draft', 'Submitted', 'Expired BT', 'Expired BCBA', 'Unlocked', 'Pending BT', 'All'];

export const SoapNoteFilters = () => {
	const [params, setParams] = useSearchParams();
	const open = params.get('filter') === 'open';
	const sessionType = params.get('sessionType') ?? '';
	const statusType = params.get('status') ?? '';

	const role = useSelector((state) => state.account.role);
	const isAdmin = role?.section.id === AdminTypes.admin;

	const closeHandler = () => {
		params.set('filter', 'close');
		setParams(params, { replace: true });
	};

	const selectStatusHandler = (name: string, id: number) => {
		params.set('page', '1');

		if (name.toLowerCase() === 'all') {
			params.delete('status');
			setParams(params, { replace: true });
			closeHandler();

			return;
		}
		params.set('status', `${id}`);
		setParams(params, { replace: true });
		closeHandler();
	};

	const sessionTypeHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		params.set('sessionType', value);
		setParams(params, { replace: true });
		closeHandler();
	};

	const data = sessionTypes.filter((x) => x.roleType === role?.section.id || isAdmin);

	return (
		<CustomDrawer
			open={open}
			onClose={closeHandler}
			label="Filters"
			isSecondary
			Content={() => (
				<>
					<Text title="Select session type" type="h6" fontWeight="medium" font="header" />
					<CustomSelect
						data={[...data, { id: '', label: 'All' }]}
						value={sessionType}
						setValue={sessionTypeHandler}
						className="marginBottom16"
					/>
					<Text title="Select status" type="h6" fontWeight="medium" font="header" />
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 16,
							alignItems: 'flex-start',
						}}
					>
						{Boolean(labels) &&
							labels.map((label, index) => {
								const type = SoapNoteStatusTypes[index] as BadgeTypes;
								const color = colors[type];

								return (
									<div
										key={index}
										style={{
											cursor: 'pointer',
											width: 'max-content',
											borderRadius: '999px',
											outline: parseInt(statusType) === index ? `1px solid ${color}` : 'none',
										}}
									>
										<Badge label={label} sizeType="lg" type={type} onClick={() => selectStatusHandler(label, index)} />
									</div>
								);
							})}
					</div>
				</>
			)}
		/>
	);
};
